/**
 * src/components/guided-tour/store/guideSlice.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  isActive: false,
  currentStepIndex: 0,
  currentStep: null,
  totalSteps: 0,
  steps: [],
  hasSeenGuide: localStorage.getItem('hasSeenInteractiveGuide') === 'true',
  isSkipped: false,
  isCompleted: false,
  guideType: 'booking', // booking, dashboard, etc.
  shouldOfferGuide: false,
  currentRoute: null,
};

const GUIDE_STEPS = {
  booking: [
    {
      id: 'select-teacher',
      target: '#teachers .teacher-card',
      titleKey: 'GuidedTourSteps.booking.selectTeacher.title',
      instructionKey: 'GuidedTourSteps.booking.selectTeacher.instruction',
      descriptionKey: 'GuidedTourSteps.booking.selectTeacher.description',
      trigger: 'click',
      route: '/',
    },
    {
      id: 'book-now',
      target: '[data-guide="book-now-button"]',
      titleKey: 'GuidedTourSteps.booking.bookNow.title',
      instructionKey: 'GuidedTourSteps.booking.bookNow.instruction',
      descriptionKey: 'GuidedTourSteps.booking.bookNow.description',
      trigger: 'click',
      route: '/teacher-details/:id',
    },
    {
      id: 'select-date',
      target: '[data-guide="calendar"]',
      titleKey: 'GuidedTourSteps.booking.selectDate.title',
      instructionKey: 'GuidedTourSteps.booking.selectDate.instruction',
      descriptionKey: 'GuidedTourSteps.booking.selectDate.description',
      trigger: 'custom',
      customAction: 'date-selected',
      route: '/teacher-details/:id',
    },
    {
      id: 'select-time',
      target: '[data-guide="time-slots"]',
      titleKey: 'GuidedTourSteps.booking.selectTime.title',
      instructionKey: 'GuidedTourSteps.booking.selectTime.instruction',
      descriptionKey: 'GuidedTourSteps.booking.selectTime.description',
      trigger: 'click',
      route: '/teacher-details/:id',
    },
    {
      id: 'fill-preferences',
      target: '[data-guide="learning-preferences"]',
      titleKey: 'GuidedTourSteps.booking.fillPreferences.title',
      instructionKey: 'GuidedTourSteps.booking.fillPreferences.instruction',
      descriptionKey: 'GuidedTourSteps.booking.fillPreferences.description',
      trigger: 'custom',
      customAction: 'preferences-completed',
      route: '/teacher-details/:id',
    },
    {
      id: 'complete-booking',
      target: '[data-guide="complete-booking"]',
      titleKey: 'GuidedTourSteps.booking.completeBooking.title',
      instructionKey: 'GuidedTourSteps.booking.completeBooking.instruction',
      descriptionKey: 'GuidedTourSteps.booking.completeBooking.description',
      trigger: 'click',
      route: '/teacher-details/:id',
    },
  ],

  studentDashboard: [
    {
      id: 'dashboard-overview',
      target: '[data-guide="main-dashboard"]',
      titleKey: 'GuidedTourSteps.studentDashboard.dashboardOverview.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.dashboardOverview.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.dashboardOverview.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'upcoming-meetings',
      target: '[data-guide="upcoming-meetings"]',
      titleKey: 'GuidedTourSteps.studentDashboard.upcomingMeetings.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.upcomingMeetings.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.upcomingMeetings.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'sidebar-navigation',
      target: '[data-guide="sidebar-menu"]',
      titleKey: 'GuidedTourSteps.studentDashboard.sidebarNavigation.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.sidebarNavigation.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.sidebarNavigation.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'profile-section',
      target: '[data-guide="profile-menu-item"]',
      titleKey: 'GuidedTourSteps.studentDashboard.profileSection.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.profileSection.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.profileSection.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'bookings-section',
      target: '[data-guide="bookings-menu-item"]',
      titleKey: 'GuidedTourSteps.studentDashboard.bookingsSection.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.bookingsSection.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.bookingsSection.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'packages-section',
      target: '[data-guide="packages-menu-item"]',
      titleKey: 'GuidedTourSteps.studentDashboard.packagesSection.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.packagesSection.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.packagesSection.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'my-packages-section',
      target: '[data-guide="my-packages-menu-item"]',
      titleKey: 'GuidedTourSteps.studentDashboard.myPackagesSection.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.myPackagesSection.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.myPackagesSection.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'payments-section',
      target: '[data-guide="payments-menu-item"]',
      titleKey: 'GuidedTourSteps.studentDashboard.paymentsSection.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.paymentsSection.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.paymentsSection.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'wallet-section',
      target: '[data-guide="wallet-menu-item"]',
      titleKey: 'GuidedTourSteps.studentDashboard.walletSection.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.walletSection.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.walletSection.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'evaluation-section',
      target: '[data-guide="evaluation-menu-item"]',
      titleKey: 'GuidedTourSteps.studentDashboard.evaluationSection.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.evaluationSection.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.evaluationSection.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'materials-section',
      target: '[data-guide="materials-menu-item"]',
      titleKey: 'GuidedTourSteps.studentDashboard.materialsSection.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.materialsSection.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.materialsSection.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'referrals-section',
      target: '[data-guide="referrals-menu-item"]',
      titleKey: 'GuidedTourSteps.studentDashboard.referralsSection.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.referralsSection.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.referralsSection.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
    {
      id: 'tour-complete',
      target: '[data-guide="dashboard-header"]',
      titleKey: 'GuidedTourSteps.studentDashboard.tourComplete.title',
      instructionKey: 'GuidedTourSteps.studentDashboard.tourComplete.instruction',
      descriptionKey: 'GuidedTourSteps.studentDashboard.tourComplete.description',
      trigger: 'button',
      route: '/Student-dashboard/dash',
    },
  ],
};

const guideSlice = createSlice({
  name: 'guide',
  initialState,
  reducers: {
    startGuide: (state, action) => {
      const { guideType = 'booking' } = action.payload || {};
      const steps = GUIDE_STEPS[guideType] || GUIDE_STEPS.booking;

      state.isActive = true;
      state.currentStepIndex = 0;
      state.currentStep = steps[0];
      state.totalSteps = steps.length;
      state.steps = steps;
      state.isSkipped = false;
      state.isCompleted = false;
      state.guideType = guideType;
    },

    nextStep: (state) => {
      if (state.currentStepIndex < state.totalSteps - 1) {
        state.currentStepIndex += 1;
        state.currentStep = state.steps[state.currentStepIndex];
      } else {
        state.isActive = false;
        state.isCompleted = true;
        state.currentStep = null;
        localStorage.setItem('hasSeenInteractiveGuide', 'true');
        state.hasSeenGuide = true;
      }
    },

    skipGuide: (state) => {
      state.isActive = false;
      state.isSkipped = true;
      state.currentStep = null;
      localStorage.setItem('hasSeenInteractiveGuide', 'true');
      state.hasSeenGuide = true;
    },

    stopGuide: (state) => {
      state.isActive = false;
      state.currentStep = null;
    },

    resetGuide: (state) => {
      state.isActive = false;
      state.currentStepIndex = 0;
      state.currentStep = null;
      state.isSkipped = false;
      state.isCompleted = false;
      localStorage.removeItem('hasSeenInteractiveGuide');
      state.hasSeenGuide = false;
    },

    updateCurrentStep: (state, action) => {
      const { step, index } = action.payload;
      state.currentStep = step;
      state.currentStepIndex = index;
    },

    setGuideRoute: (state, action) => {
      state.currentRoute = action.payload;
    },

    markStepCompleted: (state, action) => {
      const stepId = action.payload;
      const stepIndex = state.steps.findIndex((step) => step.id === stepId);

      if (stepIndex !== -1 && stepIndex === state.currentStepIndex) {
        if (stepIndex < state.totalSteps - 1) {
          state.currentStepIndex = stepIndex + 1;
          state.currentStep = state.steps[state.currentStepIndex];
        } else {
          state.isActive = false;
          state.isCompleted = true;
          state.currentStep = null;
          localStorage.setItem('hasSeenInteractiveGuide', 'true');
          state.hasSeenGuide = true;
        }
      }
    },

    checkShouldShowGuide: (state, action) => {
      const { isNewUser, hasToken } = action.payload || {};
      const shouldShow = isNewUser && !hasToken && !state.hasSeenGuide;
      if (shouldShow) state.shouldOfferGuide = true;
    },

    offerGuide: (state) => {
      state.shouldOfferGuide = true;
    },

    dismissGuideOffer: (state) => {
      state.shouldOfferGuide = false;
    },
  },
});

export const {
  startGuide,
  nextStep,
  skipGuide,
  updateCurrentStep,
  markStepCompleted,
} = guideSlice.actions;

export const selectGuideState = (state) => state.guide;
export const selectIsGuideActive = (state) => state.guide.isActive;
export const selectCurrentStep = (state) => state.guide.currentStep;

export default guideSlice.reducer;
